import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixFastgalleryItemV1Item from './fastgallery-item-v1-item-items.http.js';
// @ts-ignore
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * A Item is a ...
 * You can ...
 * Read more about Items
 * in this [article](<LINK_TO_KB_ARTICLE>).
 */
export interface Item {
  /**
   * Item ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /** @readonly */
  revision?: string | null;
  /**
   * Draft Item ID that this item originated from
   * @format GUID
   * @readonly
   */
  draftItemId?: string | null;
  /**
   * Gallery ID that the item is part of
   * @format GUID
   * @immutable
   */
  galleryId?: string | null;
  /**
   * Represents the site version when the item was published
   * @readonly
   * @immutable
   */
  siteVersion?: string | null;
  /** @maxLength 50 */
  title?: string | null;
  /** Link to an internal or external page */
  link?: Link;
  /** media of the item */
  media?: MediaItem;
  /**
   * Sort order of Item in its Gallery, needed for Internal use
   * @internal
   * @readonly
   */
  sortOrder?: number | null;
  /** @readonly */
  _createdDate?: Date | null;
  /** @readonly */
  _updatedDate?: Date | null;
  /** @maxLength 1500 */
  description?: string | null;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
}

export interface Link {
  /**
   * The type of the link
   * @internal
   */
  type?: LinkTypeWithLiterals;
  /**
   * Display text of the link.
   * `referenced_entity_field` carries this field's translations from a source entity onto an entity
   * that references it - concretely, DraftItem -> the published Item, whose translations are synced
   * from DraftItem rather than owned locally. Without it the translated label is served on the draft
   * read (so the editor looks right) and silently falls back to the origin language on the published
   * read, which is what site visitors get. It must be declared on this leaf: the collector only
   * honours the annotation on STRING / StringValue / RichContent and ignores it on message fields.
   * The path is the field's location in the source entity; every embedder of this type - pro-gallery
   * and fast-gallery, draft and published - places it at `link`, so one path is correct for all.
   * @maxLength 1000
   */
  text?: string | null;
  /**
   * Target URL of the link.
   * @format WEB_URL
   */
  url?: string | null;
  /**
   * Whether the link opens in a new tab or window. One of:
   * * `'_blank'`: The link opens in a new tab or window.
   * * `'_self'`: The link opens in the same tab or window.
   * if more options are needed, be sure to change maxLength accordingly
   * @internal
   * @maxLength 6
   */
  target?: string | null;
  /**
   * Information about the link. Reserved for internal use.
   * @internal
   */
  wixLinkData?: WixLink;
}

export enum LinkType {
  UNDEFINED = 'UNDEFINED',
  /** external link */
  EXTERNAL = 'EXTERNAL',
  /** for internal usage using wixLinkData */
  INTERNAL = 'INTERNAL',
}

/** @enumType */
export type LinkTypeWithLiterals =
  | LinkType
  | 'UNDEFINED'
  | 'EXTERNAL'
  | 'INTERNAL';

/** The link object generated by panels in the editor and used by applications in Wix */
export interface WixLink extends WixLinkLinkOneOf {
  /** External link type */
  external?: ExternalLink;
  /** Page link type */
  page?: PageLink;
  /** Anchor link type */
  anchor?: AnchorLink;
  /** Dynamic page link type */
  dynamicPage?: DynamicPageLink;
  /** Document link type */
  document?: DocumentLink;
  /** Email link type */
  email?: EmailLink;
  /** Phone link type */
  phone?: PhoneLink;
  /** Address link type */
  address?: AddressLink;
  /** WhatsApp link type */
  whatsApp?: WhatsAppLink;
  /** TPA link type */
  tpaPage?: TpaPageLink;
}

/** @oneof */
export interface WixLinkLinkOneOf {
  /** External link type */
  external?: ExternalLink;
  /** Page link type */
  page?: PageLink;
  /** Anchor link type */
  anchor?: AnchorLink;
  /** Dynamic page link type */
  dynamicPage?: DynamicPageLink;
  /** Document link type */
  document?: DocumentLink;
  /** Email link type */
  email?: EmailLink;
  /** Phone link type */
  phone?: PhoneLink;
  /** Address link type */
  address?: AddressLink;
  /** WhatsApp link type */
  whatsApp?: WhatsAppLink;
  /** TPA link type */
  tpaPage?: TpaPageLink;
}

export interface ExternalLink {
  /**
   * The url of the page
   * @format WEB_URL
   */
  url?: string;
  /**
   * Where this link should open, supports _self and _blank or any name the user chooses. _self means same page, _blank means new page.
   * @maxLength 1000
   */
  target?: string | null;
  /** @maxSize 20 */
  rel?: LinkRelWithLiterals[];
}

/**
 * The 'rel' attribute of the link. The rel attribute defines the relationship between a linked resource and the current document.
 * Further reading (also about different possible rel types): https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/rel
 * Following are the accepted 'rel' types by Wix applications.
 */
export enum LinkRel {
  /** Indicates that the current document's original author or publisher does not endorse the referenced document. */
  nofollow = 'nofollow',
  /** Instructs the browser to navigate to the target resource without granting the new browsing context access to the document that opened it. */
  noopener = 'noopener',
  /** No Referer header will be included. Additionally, has the same effect as noopener. */
  noreferrer = 'noreferrer',
  /** Indicates a link that resulted from advertisements or paid placements. */
  sponsored = 'sponsored',
}

/** @enumType */
export type LinkRelWithLiterals =
  | LinkRel
  | 'nofollow'
  | 'noopener'
  | 'noreferrer'
  | 'sponsored';

export interface PageLink {
  /**
   * The page id we want from the site
   * @maxLength 1000
   */
  pageId?: string;
  /**
   * Where this link should open, supports _self and _blank or any name the user chooses. _self means same page, _blank means new page.
   * @maxLength 1000
   */
  target?: string | null;
  /**
   * rel of link
   * @maxSize 20
   */
  rel?: LinkRelWithLiterals[];
}

export interface AnchorLink {
  /**
   * The name of the anchor
   * @maxLength 1000
   */
  anchorName?: string;
  /**
   * The data id (from the JSON page) of the anchor that should be used
   * @maxLength 1000
   */
  anchorDataId?: string;
  /**
   * The page id we want from the site
   * @maxLength 1000
   */
  pageId?: string;
  /**
   * rel of link
   * @maxSize 20
   */
  rel?: LinkRelWithLiterals[];
}

export interface DynamicPageLink {
  /**
   * The router that handles this link
   * @maxLength 1000
   */
  routerId?: string;
  /**
   * The path data we'd like
   * @maxLength 1000
   */
  innerRoute?: string;
  /**
   * The data id (from the JSON page) of the anchor that should be used
   * @maxLength 1000
   */
  anchorDataId?: string | null;
  /**
   * rel of link
   * @maxSize 20
   */
  rel?: LinkRelWithLiterals[];
}

export interface DocumentLink {
  /**
   * The id of the document
   * @maxLength 1000
   */
  docId?: string;
  /**
   * The name of the document for download purposes
   * @maxLength 1000
   */
  name?: string | null;
  /** If this document can be indexed by scrapers, default is false */
  indexable?: boolean;
}

export interface EmailLink {
  /**
   * The email we will be sending a message to
   * @format EMAIL
   */
  recipient?: string;
  /**
   * The subject of the email
   * @maxLength 1000
   */
  subject?: string | null;
  /**
   * The body of the email
   * @maxLength 100000
   */
  body?: string | null;
}

export interface PhoneLink {
  /**
   * The phone number we want to link to
   * @format PHONE
   * @maxLength 100
   */
  phoneNumber?: string;
}

export interface AddressLink {
  /**
   * An address that we can link to
   * @maxLength 1000
   */
  address?: string;
}

export interface WhatsAppLink {
  /**
   * The whatsApp phone number we want to connect with
   * @format PHONE
   */
  phoneNumber?: string;
}

/** Link to a TPA page */
export interface TpaPageLink {
  /**
   * Type of item (e.g. 'wix.stores.sub_pages.product')
   * @maxLength 1000
   */
  itemTypeIdentifier?: string;
  /**
   * Id of linked item
   * @maxLength 1000
   */
  itemId?: string;
  /**
   * Id of linked page
   * @maxLength 1000
   */
  pageId?: string;
  /**
   * Id of app being linked to (AppDefId)
   * @maxLength 1000
   */
  appDefinitionId?: string;
  /**
   * The relativepath of linked page
   * @maxLength 10000
   */
  path?: string;
  /**
   * rel of link
   * @maxSize 20
   */
  rel?: LinkRelWithLiterals[];
}

export interface MediaItem extends MediaItemMediaOneOf {
  /** image item */
  imageOptions?: Image;
  /** item's media type */
  type?: TypeWithLiterals;
}

/** @oneof */
export interface MediaItemMediaOneOf {
  /** image item */
  imageOptions?: Image;
}

export enum Type {
  /** undefined type */
  UNDEFINED = 'UNDEFINED',
  /** image */
  IMAGE = 'IMAGE',
}

/** @enumType */
export type TypeWithLiterals = Type | 'UNDEFINED' | 'IMAGE';

export interface Image {
  /** Information about the Wix Media image. */
  imageInfo?: string;
  /** Focal point of the image. */
  focalPoint?: Point;
  /** Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif). */
  exif?: Record<string, any> | null;
  /**
   * Image compression level. <br />
   *
   * Min: `30` <br />
   * Max: `100`
   * @min 30
   * @max 100
   */
  quality?: number | null;
  /** [Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image. */
  unsharpMasking?: UnsharpMasking;
  /**
   * Whether the image is saved in secure media.
   * @internal
   */
  secure?: boolean | null;
  /**
   * Image alt text, translatable.
   * @maxLength 5000
   */
  altText?: string | null;
}

export interface FocalPoint {
  /** X-coordinate of the focal point. */
  x?: number;
  /** Y-coordinate of the focal point. */
  y?: number;
}

export interface Point {
  /** X-coordinate of the focal point. */
  x?: number;
  /** Y-coordinate of the focal point. */
  y?: number;
}

export interface UnsharpMasking {
  /**
   * Unsharp masking amount. Controls the sharpening strength. <br />
   *
   * Min: `0` <br />
   * Max: `5`
   * @max 5
   */
  amount?: number | null;
  /** Unsharp masking radius in pixels. Controls the sharpening width. */
  radius?: number | null;
  /**
   * Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />
   *
   * Min: `0` <br />
   * Max: `1`
   * @max 1
   */
  threshold?: number | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface CreateItemRequest {
  /** Item to be created. */
  item: Item;
}

export interface CreateItemResponse {
  /** The created Item. */
  item?: Item;
}

export interface GetItemRequest {
  /**
   * ID of the Item to retrieve.
   * @format GUID
   */
  itemId: string;
}

export interface GetItemResponse {
  /** The requested Item. */
  item?: Item;
}

export interface UpdateItemRequest {
  /** Item to be updated, may be partial. */
  item: Item;
  /**
   * Set of fields to update.
   *
   * Fields that aren't included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateItemResponse {
  /** Updated Item. */
  item?: Item;
}

export interface DeleteItemRequest {
  /**
   * Id of the Item to delete.
   * @format GUID
   */
  itemId: string;
}

export interface DeleteItemResponse {}

export interface QueryItemsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryItemsResponse {
  /** List of Items. */
  items?: Item[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  _id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Updated Item. */
  item?: Item;
}

export interface BulkCreateItemsRequest {
  /**
   * List of Items to be created
   * @minSize 1
   * @maxSize 100
   */
  items: Item[];
  /** set to `true` if you wish to receive back the created Items in the response */
  returnEntity?: boolean;
}

export interface BulkCreateItemsResponse {
  /**
   * List of the bulk create operation results including the Items and metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkItemResult[];
  /** Metadata regarding the bulk create operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkItemResult {
  /** Metadata regarding the specific single create operation */
  itemMetadata?: ItemMetadata;
  /** Only exists if `returnEntity` was set to true in the request */
  item?: Item;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateItemsRequest {
  /**
   * List of Items to be updated.
   * @minSize 1
   * @maxSize 100
   */
  items: MaskedItem[];
  /** set to `true` if you wish to receive back the updated Items in the response */
  returnEntity?: boolean;
}

export interface MaskedItem {
  /** Item to be updated, may be partial */
  item?: Item;
  /**
   * Explicit list of fields to update.
   * @internal
   */
  fieldMask?: string[];
}

export interface BulkUpdateItemsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateItemsResponseBulkItemResult[];
  /** Metadata regarding the bulk update operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateItemsResponseBulkItemResult {
  /** Metadata regarding the specific single update operation */
  itemMetadata?: ItemMetadata;
  /** Only exists if `returnEntity` was set to true in the request */
  item?: Item;
}

export interface BulkDeleteItemsRequest {
  /**
   * Item ids to be deleted
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  itemIds: string[];
}

export interface BulkDeleteItemsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkDeleteItemsResponseBulkItemResult[];
  /** Metadata regarding the bulk delete operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteItemsResponseBulkItemResult {
  /** Metadata regarding the specific single delete operation */
  itemMetadata?: ItemMetadata;
}

export interface RemoveItemFromTrashBinRequest {
  /**
   * ID of the Item to remove from TrashBin.
   * @format GUID
   */
  itemId: string;
}

export interface RemoveItemFromTrashBinResponse {}

export interface RestoreItemFromTrashBinRequest {
  /**
   * ID of the Item to restore from TrashBin.
   * @format GUID
   */
  itemId: string;
}

export interface RestoreItemFromTrashBinResponse {
  /** Restored Item. */
  item?: Item;
}

export interface GetDeletedItemRequest {
  /**
   * ID of the Item to retrieve from TrashBin.
   * @format GUID
   */
  itemId: string;
}

export interface GetDeletedItemResponse {
  /** Deleted Item. */
  item?: Item;
}

export interface ListDeletedItemsRequest {
  /** WQL query to run against TrashBin items */
  query?: CursorQuery;
  /**
   * List of Item IDs to retrieve from TrashBin. Supersedes query if both are present.
   * @format GUID
   * @maxSize 100
   */
  itemIds?: string[];
}

export interface ListDeletedItemsResponse {
  /** List of Items in TrashBin. */
  items?: Item[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/**
 * create item (todo: tech writer to add more details)
 * @param item - Item to be created.
 * @public
 * @documentationMaturity preview
 * @requiredField item
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @returns The created Item.
 * @fqn wix.fastgallery.item.v1.ItemService.CreateItem
 */
export async function createItem(
  item: Item
): Promise<
  NonNullablePaths<
    Item,
    | `media.imageOptions.focalPoint.x`
    | `media.imageOptions.focalPoint.y`
    | `media.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ item: item }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'item.media.imageOptions.imageInfo' }],
      },
    ]
  );

  const reqOpts = ambassadorWixFastgalleryItemV1Item.createItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'item.media.imageOptions.imageInfo' }],
        },
      ])
    )?.item!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { item: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['item']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a Item.
 * @param itemId - ID of the Item to retrieve.
 * @public
 * @documentationMaturity preview
 * @requiredField itemId
 * @permissionId FAST_GALLERY.READ
 * @applicableIdentity APP
 * @returns The requested Item.
 * @fqn wix.fastgallery.item.v1.ItemService.GetItem
 */
export async function getItem(
  itemId: string
): Promise<
  NonNullablePaths<
    Item,
    | `media.imageOptions.focalPoint.x`
    | `media.imageOptions.focalPoint.y`
    | `media.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ itemId: itemId });

  const reqOpts = ambassadorWixFastgalleryItemV1Item.getItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'item.media.imageOptions.imageInfo' }],
        },
      ])
    )?.item!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { itemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['itemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a Item.
 *
 * Each time the Item is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the Item.
 * This ensures you're working with the latest Item
 * and prevents unintended overwrites.
 * @param _id - Item ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField item
 * @requiredField item.revision
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @returns Updated Item.
 * @fqn wix.fastgallery.item.v1.ItemService.UpdateItem
 */
export async function updateItem(
  _id: string,
  item: NonNullablePaths<UpdateItem, `revision`, 2>
): Promise<
  NonNullablePaths<
    Item,
    | `media.imageOptions.focalPoint.x`
    | `media.imageOptions.focalPoint.y`
    | `media.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ item: { ...item, id: _id } }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'item.media.imageOptions.imageInfo' }],
      },
    ]
  );

  const reqOpts = ambassadorWixFastgalleryItemV1Item.updateItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'item.media.imageOptions.imageInfo' }],
        },
      ])
    )?.item!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { item: '$[1]' },
        explicitPathsToArguments: { 'item.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'item']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateItem {
  /**
   * Item ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /** @readonly */
  revision?: string | null;
  /**
   * Draft Item ID that this item originated from
   * @format GUID
   * @readonly
   */
  draftItemId?: string | null;
  /**
   * Gallery ID that the item is part of
   * @format GUID
   * @immutable
   */
  galleryId?: string | null;
  /**
   * Represents the site version when the item was published
   * @readonly
   * @immutable
   */
  siteVersion?: string | null;
  /** @maxLength 50 */
  title?: string | null;
  /** Link to an internal or external page */
  link?: Link;
  /** media of the item */
  media?: MediaItem;
  /**
   * Sort order of Item in its Gallery, needed for Internal use
   * @internal
   * @readonly
   */
  sortOrder?: number | null;
  /** @readonly */
  _createdDate?: Date | null;
  /** @readonly */
  _updatedDate?: Date | null;
  /** @maxLength 1500 */
  description?: string | null;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
}

/**
 * delete item (todo: tech writer to add more details)
 * @param itemId - Id of the Item to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField itemId
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.item.v1.ItemService.DeleteItem
 */
export async function deleteItem(itemId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ itemId: itemId });

  const reqOpts = ambassadorWixFastgalleryItemV1Item.deleteItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { itemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['itemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of gallery items.
 *
 * The `queryItems()` method builds a query to retrieve a list of gallery items and returns an `ItemsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `ItemsQueryBuilder` methods onto the query. `ItemsQueryBuilder` methods enable you to filter, sort, and control the results that `queryItems()` returns.
 * @public
 * @documentationMaturity preview
 * @permissionId FAST_GALLERY.READ
 * @applicableIdentity APP
 * @fqn wix.fastgallery.item.v1.ItemService.QueryItems
 */
export function queryItems(): ItemsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<Item, 'CURSOR', QueryItemsRequest, QueryItemsResponse>({
    func: async (payload: QueryItemsRequest) => {
      const reqOpts = ambassadorWixFastgalleryItemV1Item.queryItems(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryItemsRequest['query']) => {
      const args = [query, {}] as [QueryItemsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryItemsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [{ path: 'items.media.imageOptions.imageInfo' }],
          },
        ])
      );

      return {
        items: transformedData?.items,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface ItemsQueryResult extends QueryCursorResult {
  items: Item[];
  query: ItemsQueryBuilder;
  next: () => Promise<ItemsQueryResult>;
  prev: () => Promise<ItemsQueryResult>;
}

export interface ItemsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_id'
      | 'draftItemId'
      | 'galleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_id'
      | 'draftItemId'
      | 'galleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName:
      | '_id'
      | 'draftItemId'
      | 'galleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName:
      | '_id'
      | 'draftItemId'
      | 'galleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName:
      | '_id'
      | 'draftItemId'
      | 'galleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName:
      | '_id'
      | 'draftItemId'
      | 'galleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName: '_id' | 'draftItemId' | 'galleryId' | 'title',
    value: string
  ) => ItemsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_id'
      | 'draftItemId'
      | 'galleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ItemsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_id'
      | 'draftItemId'
      | 'galleryId'
      | 'title'
      | '_createdDate'
      | '_updatedDate',
    value: boolean
  ) => ItemsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | 'draftItemId'
      | 'galleryId'
      | 'title'
      | 'sortOrder'
      | '_createdDate'
      | '_updatedDate'
    >
  ) => ItemsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | 'draftItemId'
      | 'galleryId'
      | 'title'
      | 'sortOrder'
      | '_createdDate'
      | '_updatedDate'
    >
  ) => ItemsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => ItemsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => ItemsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<ItemsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.fastgallery.item.v1.ItemService.QueryItems
 * @requiredField query
 */
export async function typedQueryItems(
  query: ItemQuery
): Promise<
  NonNullablePaths<
    QueryItemsResponse,
    `items` | `items.${number}.media.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixFastgalleryItemV1Item.queryItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'items.media.imageOptions.imageInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ItemQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'draftItemId',
        'galleryId',
        'title'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<Item, ItemQuerySpec>;
export type ItemQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<Item, ItemQuerySpec, ItemQuery>(),
};

/**
 * Updates extended fields of a Item without incrementing revision
 * @param _id - ID of the entity to update.
 * @param namespace - Identifier for the app whose extended fields are being updated.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField namespace
 * @requiredField options
 * @requiredField options.namespaceData
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.item.v1.ItemService.UpdateExtendedFields
 */
export async function updateExtendedFields(
  _id: string,
  namespace: string,
  options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
): Promise<
  NonNullablePaths<
    UpdateExtendedFieldsResponse,
    | `item.media.imageOptions.focalPoint.x`
    | `item.media.imageOptions.focalPoint.y`
    | `item.media.type`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    namespace: namespace,
    namespaceData: options?.namespaceData,
  });

  const reqOpts =
    ambassadorWixFastgalleryItemV1Item.updateExtendedFields(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'item.media.imageOptions.imageInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          namespace: '$[1]',
          namespaceData: '$[2].namespaceData',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'namespace', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateExtendedFieldsOptions {
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

/**
 * Create multiple Items in a single request. Works synchronously.
 * @param items - List of Items to be created
 * @public
 * @documentationMaturity preview
 * @requiredField items
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.item.v1.ItemService.BulkCreateItems
 */
export async function bulkCreateItems(
  items: Item[],
  options?: BulkCreateItemsOptions
): Promise<
  NonNullablePaths<
    BulkCreateItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.media.type`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      items: items,
      returnEntity: options?.returnEntity,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'items.media.imageOptions.imageInfo' }],
      },
    ]
  );

  const reqOpts = ambassadorWixFastgalleryItemV1Item.bulkCreateItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'results.item.media.imageOptions.imageInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          items: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['items', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateItemsOptions {
  /** set to `true` if you wish to receive back the created Items in the response */
  returnEntity?: boolean;
}

/**
 * Update multiple Items in a single request. Works synchronously.
 * @param items - List of Items to be updated.
 * @public
 * @documentationMaturity preview
 * @requiredField items
 * @requiredField items.item.revision
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.item.v1.ItemService.BulkUpdateItems
 */
export async function bulkUpdateItems(
  items: NonNullablePaths<MaskedItem, `item.revision`, 3>[],
  options?: BulkUpdateItemsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.media.type`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      items: items,
      returnEntity: options?.returnEntity,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'items.item.media.imageOptions.imageInfo' }],
      },
    ]
  );

  const reqOpts = ambassadorWixFastgalleryItemV1Item.bulkUpdateItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'results.item.media.imageOptions.imageInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          items: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['items', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateItemsOptions {
  /** set to `true` if you wish to receive back the updated Items in the response */
  returnEntity?: boolean;
}

/**
 * Delete multiple Items in a single request. Works synchronously.
 * @param itemIds - Item ids to be deleted
 * @public
 * @documentationMaturity preview
 * @requiredField itemIds
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.item.v1.ItemService.BulkDeleteItems
 */
export async function bulkDeleteItems(
  itemIds: string[]
): Promise<
  NonNullablePaths<
    BulkDeleteItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ itemIds: itemIds });

  const reqOpts = ambassadorWixFastgalleryItemV1Item.bulkDeleteItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { itemIds: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['itemIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Removes deleted Item from TrashBin
 * @param itemId - ID of the Item to remove from TrashBin.
 * @public
 * @documentationMaturity preview
 * @requiredField itemId
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.item.v1.ItemService.RemoveItemFromTrashBin
 */
export async function removeItemFromTrashBin(itemId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ itemId: itemId });

  const reqOpts =
    ambassadorWixFastgalleryItemV1Item.removeItemFromTrashBin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { itemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['itemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Restores an Item from TrashBin
 * @param itemId - ID of the Item to restore from TrashBin.
 * @public
 * @documentationMaturity preview
 * @requiredField itemId
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.item.v1.ItemService.RestoreItemFromTrashBin
 */
export async function restoreItemFromTrashBin(
  itemId: string
): Promise<
  NonNullablePaths<
    RestoreItemFromTrashBinResponse,
    | `item.media.imageOptions.focalPoint.x`
    | `item.media.imageOptions.focalPoint.y`
    | `item.media.type`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ itemId: itemId });

  const reqOpts =
    ambassadorWixFastgalleryItemV1Item.restoreItemFromTrashBin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'item.media.imageOptions.imageInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { itemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['itemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Get deleted Item from TrashBin
 * @param itemId - ID of the Item to retrieve from TrashBin.
 * @public
 * @documentationMaturity preview
 * @requiredField itemId
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.item.v1.ItemService.GetDeletedItem
 */
export async function getDeletedItem(
  itemId: string
): Promise<
  NonNullablePaths<
    GetDeletedItemResponse,
    | `item.media.imageOptions.focalPoint.x`
    | `item.media.imageOptions.focalPoint.y`
    | `item.media.type`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ itemId: itemId });

  const reqOpts = ambassadorWixFastgalleryItemV1Item.getDeletedItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'item.media.imageOptions.imageInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { itemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['itemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * List deleted Items which are in TrashBin
 * @public
 * @documentationMaturity preview
 * @permissionId FAST_GALLERY.MANAGE
 * @applicableIdentity APP
 * @fqn wix.fastgallery.item.v1.ItemService.ListDeletedItems
 */
export async function listDeletedItems(
  options?: ListDeletedItemsOptions
): Promise<
  NonNullablePaths<
    ListDeletedItemsResponse,
    `items` | `items.${number}.media.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: options?.query,
    itemIds: options?.itemIds,
  });

  const reqOpts = ambassadorWixFastgalleryItemV1Item.listDeletedItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'items.media.imageOptions.imageInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          query: '$[0].query',
          itemIds: '$[0].itemIds',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListDeletedItemsOptions {
  /** WQL query to run against TrashBin items */
  query?: CursorQuery;
  /**
   * List of Item IDs to retrieve from TrashBin. Supersedes query if both are present.
   * @format GUID
   * @maxSize 100
   */
  itemIds?: string[];
}
